
import { Lesson as LessonComponent } from "./Lesson";
import { Lesson, UserProgress } from "@/types/lesson";
import { Flag } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface LessonCardProps {
  lesson: Lesson;
  isLocked: boolean; // Keeping for type compatibility
  progress?: UserProgress;
  onClick: () => void;
  previousLessonCompleted: boolean; // Keeping for type compatibility
}

/**
 * LessonCard component - Enhanced wrapper around the Lesson component
 * Shows a visual flag indicator and progress information:
 * - Green flag: Lesson has content
 * - Red flag: Lesson has no content or empty content
 * - Progress bar: Shows completion status
 * - Ready/Start/Continue indicators based on progress
 */
export const LessonCard = ({ 
  lesson, 
  progress, 
  onClick
}: LessonCardProps) => {
  // Enhanced content detection with better debugging
  const checkHasContent = () => {
    console.log("🔍 LessonCard content check for:", lesson.title);
    console.log("🔍 Raw lesson content:", lesson.content);
    console.log("🔍 Content type:", typeof lesson.content);
    
    if (!lesson.content) {
      console.log("❌ No content property");
      return false;
    }
    
    if (lesson.content === '' || lesson.content === '{}') {
      console.log("❌ Empty content string or empty object string");
      return false;
    }
    
    // Try to parse if it's a string
    if (typeof lesson.content === 'string') {
      try {
        const parsed = JSON.parse(lesson.content);
        console.log("🔍 Parsed content:", parsed);
        
        // Check for various content patterns
        const hasDirectSections = parsed.sections && Array.isArray(parsed.sections) && parsed.sections.length > 0;
        const hasNestedSections = parsed.content?.sections && Array.isArray(parsed.content.sections) && parsed.content.sections.length > 0;
        const hasTextContent = parsed.text || parsed.content?.text;
        
        const result = hasDirectSections || hasNestedSections || hasTextContent;
        console.log("✅ String content check result:", { hasDirectSections, hasNestedSections, hasTextContent, result });
        return result;
      } catch (e) {
        console.log("⚠️ Failed to parse content as JSON, treating as plain text");
        return lesson.content.trim().length > 0;
      }
    }
    
    // If it's already an object
    if (typeof lesson.content === 'object') {
      const obj = lesson.content as any;
      const hasDirectSections = obj.sections && Array.isArray(obj.sections) && obj.sections.length > 0;
      const hasNestedSections = obj.content?.sections && Array.isArray(obj.content.sections) && obj.content.sections.length > 0;
      const hasTextContent = obj.text || obj.content?.text;
      
      const result = hasDirectSections || hasNestedSections || hasTextContent;
      console.log("✅ Object content check result:", { hasDirectSections, hasNestedSections, hasTextContent, result });
      return result;
    }
    
    console.log("❌ Unknown content format");
    return false;
  };

  const hasContent = checkHasContent();

  // Progress status checks with proper null/undefined handling
  const isCompleted = progress?.status === 'completed';
  const isInProgress = progress?.status === 'in_progress';
  const isNotStarted = !progress || progress?.status === 'not_started';

  console.log("LessonCard content and progress check:", {
    lessonId: lesson.id,
    title: lesson.title,
    hasContent: hasContent,
    progressStatus: progress?.status || 'no progress found',
    progressData: progress,
    isCompleted,
    isInProgress,
    isNotStarted
  });

  // Calculate progress percentage based on status
  const getProgressPercentage = () => {
    if (isCompleted) return 100;
    if (isInProgress) return 50; // Assume 50% for in-progress
    return 0; // Not started
  };

  // Get appropriate status text and color
  const getStatusInfo = () => {
    if (isCompleted) {
      return { text: "Completed", color: "text-green-600", bgColor: "bg-green-50" };
    }
    if (isInProgress) {
      return { text: "Continue learning", color: "text-blue-600", bgColor: "bg-blue-50" };
    }
    if (hasContent) {
      return { text: "Start learning", color: "text-primary", bgColor: "bg-primary/10" };
    }
    return { text: "Content coming soon", color: "text-gray-600", bgColor: "bg-gray-50" };
  };

  // Always show flag - green for content, red for no content
  const flagColor = hasContent ? 'bg-green-500' : 'bg-red-500';
  const flagTitle = hasContent ? 'Has content' : 'Missing content';
  const statusInfo = getStatusInfo();
  const progressPercentage = getProgressPercentage();

  return (
    <div className="relative">
      {/* Flag indicator - green for content, red for missing content */}
      <div 
        className={`absolute -top-2 -right-2 z-10 ${flagColor} text-white rounded-full p-1 shadow-lg`}
        title={flagTitle}
      >
        <Flag className="h-3 w-3" />
      </div>
      
      {/* Enhanced Lesson component with progress overlay */}
      <div className="relative">
        <LessonComponent
          lesson={lesson}
          isLocked={false} // Always unlocked
          isCompleted={isCompleted}
          isInProgress={isInProgress}
          previousLessonCompleted={true} // Always considered completed
          onClick={onClick}
        />
        
        {/* Progress overlay at the bottom of the card */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-sm border-t border-gray-100">
          <div className="space-y-2">
            {/* Progress bar */}
            <div className="space-y-1">
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500">Progress</span>
                <span className="font-medium text-gray-700">{progressPercentage}%</span>
              </div>
              <Progress 
                value={progressPercentage} 
                className="h-2"
              />
            </div>
            
            {/* Status indicator */}
            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
              {statusInfo.text}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
